import {
  Box,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type { TypographyProps } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import type { ColumnMetadata } from "../add-stat-card-modal/utils";

type FilterOption = { label: string; value: string | number };

interface ReportFilterSectionProps {
  expanded: boolean;
  title: string;
  titleVariant?: TypographyProps["variant"];
  titleColor?: TypographyProps["color"];
  filterColumn: string;
  filterValues: string[];
  availableColumns?: ColumnMetadata[];
  filterableColumns?: ColumnMetadata[];
  filterColumnOptions: FilterOption[];
  dynamicFilterLoading: boolean;
  onToggle: () => void;
  onFilterColumnChange: (value: string) => void;
  onFilterValuesChange: (values: string[]) => void;
}

export const ReportFilterSection = ({
  expanded,
  title,
  titleVariant = "subtitle2",
  titleColor,
  filterColumn,
  filterValues,
  availableColumns = [],
  filterableColumns,
  filterColumnOptions,
  dynamicFilterLoading,
  onToggle,
  onFilterColumnChange,
  onFilterValuesChange,
}: ReportFilterSectionProps) => {
  // Use filterableColumns if provided, otherwise filter availableColumns to exclude dates
  const columnsForFilter = filterableColumns || availableColumns.filter((column) => !column.isDate);

  return (
    <Box>
      <Button
        onClick={onToggle}
        size="small"
        sx={{ p: 0, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        <Typography
          variant={titleVariant}
          sx={{ fontWeight: 600 }}
          color={titleColor}
        >
          {title}
        </Typography>
      </Button>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small" sx={{ flex: 1 }}>
            <InputLabel id="report-filter-column-label">Filter Column</InputLabel>
            <Select
              labelId="report-filter-column-label"
              value={filterColumn}
              label="Filter Column"
              onChange={(event) => onFilterColumnChange(event.target.value)}
            >
              {columnsForFilter.map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  {column.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {filterColumn && (
            <Box
              sx={{
                flex: 1,
                pl: 1,
                borderLeft: "3px solid",
                borderLeftColor: "divider",
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="report-filter-values-label">Filter Values</InputLabel>
                <Select
                  labelId="report-filter-values-label"
                  multiple
                  value={filterValues}
                  label="Filter Values"
                  onChange={(event) => {
                    const value = event.target.value;
                    onFilterValuesChange(
                      typeof value === "string" ? value.split(",") : value
                    );
                  }}
                >
                  {dynamicFilterLoading ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : filterColumnOptions.length > 0 ? (
                    filterColumnOptions.map((option) => (
                      <MenuItem key={String(option.value)} value={String(option.value)}>
                        {option.label}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No options available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};